// Option E — the objective column beside the subjective one.
// A self-reported score can drift simply because the participant has learnt the
// questions. Waist, BP, HbA1c and the weekly 0–10 symptom score cannot, which is
// what makes this panel worth the free-text parsing it needs.
import React from "react";
import { useTranslation } from "react-i18next";
import { Section, Empty, Label, Delta } from "@/components/clinical/kit";
import { MEASURE_COL_LABELS, measureTable, symptomTrend } from "@/config/clinical";

/** 0–10 severity over up to 13 points. Lower is better here — this is severity. */
function Sparkline({ points }) {
  const vals = points.map((p) => p.value);
  const idx = vals.map((v, i) => (v == null ? null : i)).filter((i) => i != null);
  if (idx.length < 2) return null;
  const W = 240, H = 44, PAD = 4;
  const x = (i) => PAD + (i / (points.length - 1)) * (W - PAD * 2);
  const y = (v) => PAD + (1 - v / 10) * (H - PAD * 2);
  const d = idx.map((i, n) => `${n === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(vals[i]).toFixed(1)}`).join(" ");
  const first = vals[idx[0]], last = vals[idx[idx.length - 1]];
  const stroke = last < first ? "#15803D" : last > first ? "#DC2626" : "#78716c";
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-label="symptom trend"
      className="overflow-visible">
      <line x1={PAD} y1={y(5)} x2={W - PAD} y2={y(5)} stroke="#EFE9DE" strokeWidth="1" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {idx.map((i) => <circle key={i} cx={x(i)} cy={y(vals[i])} r="2.2" fill={stroke} />)}
    </svg>
  );
}

export default function MeasuresPanel({ worksheetsByStage }) {
  const { t } = useTranslation();
  const rows = measureTable(
    Object.fromEntries(Object.entries(worksheetsByStage || {}).map(([s, w]) => [s, w?.w32 || {}])));
  const trends = symptomTrend(
    Object.fromEntries(Object.entries(worksheetsByStage || {}).map(([s, w]) => [s, w?.w33 || {}])));
  const filled = rows.filter((r) => !r.empty);

  return (
    <Section title="Objective measures and symptom trend" sub="Worksheets 32 and 33" testid="measures-panel">
      {filled.length === 0 ? (
        <Empty>No measurements recorded yet.</Empty>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[#E5DFD3]">
          <table className="w-full min-w-[560px] text-sm">
            <thead className="bg-[#E8EFE9] text-left">
              <tr>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t("Measure")}</th>
                {MEASURE_COL_LABELS.map((c) => (
                  <th key={c} className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-stone-500">{t(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filled.map((r) => (
                <tr key={r.i} className="border-t border-[#EFE9DE]" data-testid={`measure-${r.i}`}>
                  <td className="px-3 py-1.5 font-medium text-stone-700">{t(r.label)}</td>
                  {r.cells.map((c, i) => (
                    <td key={i} className="px-3 py-1.5 font-mono tabular-nums text-stone-700">{c || "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-5">
        <Label>Weekly symptom severity · 0 absent, 10 worst</Label>
        {trends.length === 0 ? (
          <div className="mt-1"><Empty>No symptoms are being tracked on worksheet 33.</Empty></div>
        ) : (
          <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {trends.map((s, i) => (
              <div key={i} className="rounded-md border border-[#E5DFD3] p-3" data-testid={`trend-${i}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="truncate text-sm font-medium text-stone-800">
                    {s.name || `${t("Symptom")} ${i + 1}`}
                  </span>
                  {/* severity: a fall is an improvement, so the sign is inverted */}
                  <Delta value={s.first != null && s.last != null && s.count > 1
                    ? Math.round((s.first - s.last) * 10) / 10 : null} />
                </div>
                <div className="mt-1 font-mono text-[11px] text-stone-500">
                  {s.count > 0 ? `${s.first} → ${s.last} · ${s.count} ${t("entries")}` : t("no entries")}
                </div>
                <div className="mt-1"><Sparkline points={s.points} /></div>
              </div>
            ))}
          </div>
        )}
        <p className="mt-2 text-[11px] text-stone-400">
          {t("Severity falls as the participant improves, so a downward line and a green figure are good here.")}
        </p>
      </div>
    </Section>
  );
}
